import React, { useEffect, useState } from 'react';
import {
  Card,
  Dropdown,
  Row,
  Col,
  ButtonGroup,
  Button,
  Image
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import CardDropdown from 'components/common/CardDropdown';
import PageHeader from 'components/common/PageHeader';
import { getAllByActive } from 'services/sites';

const columns = handleEdit => [
  {
    accessorKey: 'none',
    header: 'opciones',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' } // <-- aquí cambia text-end por text-center
    },
    cell: ({ row: { original } }) => (
      <CardDropdown drop={'end'}>
        <div className="py-2">
          <Dropdown.Item onClick={() => handleEdit(original.id)}>
            Editar
          </Dropdown.Item>
        </div>
      </CardDropdown>
    )
  },
  {
    accessorKey: 'name',
    header: 'Nombre',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) => <span>{original.name}</span>
  },
  {
    accessorKey: 'image_url',
    header: 'Imagen',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) =>
      original.image_url ? (
        <Image
          src={original.image_url}
          alt={original.name}
          rounded
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
      ) : (
        <span className="text-muted">Sin imagen</span>
      )
  },
  {
    accessorKey: 'header_color',
    header: 'Color Header',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) =>
      original.header_color ? (
        <span
          style={{
            display: 'inline-block',
            width: 24,
            height: 24,
            borderRadius: 4,
            background: original.header_color,
            border: '1px solid #ccc'
          }}
          title={original.header_color}
        ></span>
      ) : (
        <span className="text-muted">No configurado</span>
      )
  },
  {
    accessorKey: 'footer_color',
    header: 'Color Footer',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) =>
      original.footer_color ? (
        <span
          style={{
            display: 'inline-block',
            width: 24,
            height: 24,
            borderRadius: 4,
            background: original.footer_color,
            border: '1px solid #ccc'
          }}
          title={original.footer_color}
        ></span>
      ) : (
        <span className="text-muted">No configurado</span>
      )
  },
  {
    accessorKey: 'domain_name',
    header: 'Dominio',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) => (
      <span>
        {original.domain_name || (
          <span className="text-muted">No configurado</span>
        )}
      </span>
    )
  },
  {
    accessorKey: 'is_active',
    header: 'Estado',
    meta: {
      headerProps: { className: 'text-center text-900' },
      cellProps: { className: 'py-2 text-center' }
    },
    cell: ({ row: { original } }) =>
      original.is_active ? (
        <span className="badge bg-success">Activo</span>
      ) : (
        <span className="badge bg-secondary">Inactivo</span>
      )
  }
];

const SitesList = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(1); // 1: activos, 0: inactivos
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    getAllByActive(filter)
      .then(response => {
        setSites(Array.isArray(response) ? response : []);
        setLoading(false);
      })
      .catch(() => {
        setSites([]);
        setLoading(false);
      });
  }, [filter]);

  const handleEdit = id => {
    localStorage.setItem('selectedSiteId', id);
    navigate(`/sites/edit`);
  };

  const table = useAdvanceTable({
    data: sites,
    columns: columns(handleEdit),
    sortable: true,
    pagination: true,
    perPage: 5
  });

  return (
    <>
      <PageHeader
        title="Sitios"
        description="Gestión de sitios en el sistema"
        className="mb-3"
      />

      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="g-3 align-items-center">
              <Col xs={12} md={6} lg={4}>
                <h5 className="mb-0 text-center text-md-start">
                  Gestión de Sitios
                </h5>
              </Col>
              <Col xs={12} md="auto" className="text-center">
                <ButtonGroup size="sm">
                  <Button
                    variant={filter === 1 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(1)}
                  >
                    Activos
                  </Button>
                  <Button
                    variant={filter === 0 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(0)}
                  >
                    Inactivos
                  </Button>
                </ButtonGroup>
              </Col>
              <Col xs={12} md={6} lg={4} className="text-center text-md-end">
                <AdvanceTableSearchBox />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-3">Cargando sitios...</div>
            ) : (
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  bordered: true, // Bootstrap: agrega bordes a todas las celdas
                  className:
                    'fs-10 mb-0 overflow-hidden table-bordered table-hover', // table-bordered y table-hover para mejor diseño
                  style: {
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }
                }}
              />
            )}
          </Card.Body>
          <Card.Footer>
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
};

export default SitesList;
